import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiCall, endpoints } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        try {
          const response = await apiCall(endpoints.user, { method: 'GET' });
          const userData = response.data || response;
          setUser(userData);
          setToken(savedToken);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('auth_token');
          setToken(null);
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      
      const response = await apiCall(endpoints.login, {
        method: 'POST',
        data: {
          email,
          password,
        },
      });

      // Handle the API response structure from AuthController
      const authToken = response.data?.access_token;
      const userData = response.data?.user;

      if (!authToken) {
        console.error('Token extraction failed. Response:', response);
        throw new Error('No authentication token received');
      }

      localStorage.setItem('auth_token', authToken);
      setToken(authToken);
      setUser(userData);
      setIsAuthenticated(true);
      
      return { 
        success: true, 
        message: response.message || 'Login successful',
        user: userData
      };
      
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.message || 'Login failed. Please check your credentials.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      
      const response = await apiCall(endpoints.register, {
        method: 'POST',
        data: {
          name: `${userData.firstName} ${userData.lastName}`,
          email: userData.email,
          password: userData.password,
          password_confirmation: userData.confirmPassword,
          first_name: userData.firstName,
          last_name: userData.lastName,
          phone: userData.phone,
        },
      });

      // Handle the API response structure from AuthController
      const authToken = response.data?.access_token;
      const userInfo = response.data?.user;

      if (!authToken) {
        console.error('Token extraction failed. Response:', response);
        throw new Error('No authentication token received');
      }

      localStorage.setItem('auth_token', authToken);
      setToken(authToken);
      setUser(userInfo);
      setIsAuthenticated(true);
      
      return { 
        success: true, 
        message: response.message || 'Registration successful',
        user: userInfo
      };
      
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.message || 'Registration failed. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await apiCall(endpoints.logout, {
          method: 'POST',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setIsLoading(true);
      
      const response = await apiCall('/user/profile', {
        method: 'PUT',
        data: profileData,
      });

      const userData = response.data || response.user;
      setUser(userData);
      return { success: true, message: 'Profile updated successfully' };
      
    } catch (error) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        message: error.message || 'Profile update failed' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if user is admin
  const isAdmin = () => {
    return user && (user.is_admin === true || user.role === 'admin');
  };

  // Helper function to check if user has admin access
  const hasAdminAccess = () => {
    return isAuthenticated && isAdmin();
  };

  // Helper function to get the default redirect path based on user role
 

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    isAdmin: isAdmin(),
    hasAdminAccess: hasAdminAccess(),
   
    login,
    register,
    logout,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };